import { ReduceStore } from 'flux/utils';
import AppDispatcher from '~/dispatchers/app_dispatcher';
import JobActionTypes from '~/types/job_action_types';

class JobStore extends ReduceStore {
  constructor() {
    super(AppDispatcher);
  }

  getInitialState() {
    return {
      lastJobStoreAction:       null,
      errors:                   {},
      submitting:               false,
      loadingJobs:              false,
      loadingJob:               false,
      job:                      {},
      jobs:                     [],
      pagination:               {},
      scopedRecords:            { jobs: [] },
      scopedLoadings:           { jobs: false },
      scopedQueries:            { jobs: {} },
      ownerScope:               'all',
      jobScopeSize:             parseInt(localStorage.jobScopeSize, 10) || 10,
      candidateScopeSize:       parseInt(localStorage.candidateScopeSize, 10) || 10,
      jobFormDropdownsData:     {},
      leadJobApplications:      [],
      leadJobApplicationsQuery: {},
    };
  }

  reduce(state, action) {
    switch (action.type) {
      case JobActionTypes.LOAD_JOBS:
        return this.loadJobs(state, action);

      case JobActionTypes.LOAD_JOBS_DONE:
        return this.loadJobsDone(state, action);

      case JobActionTypes.LOAD_JOBS_FAIL:
        return this.loadJobsFail(state, action);

      case JobActionTypes.LOAD_JOB:
        return this.loadJob(state, action);

      case JobActionTypes.LOAD_JOB_DONE:
        return this.loadJobDone(state, action);

      case JobActionTypes.LOAD_JOB_FAIL:
        return this.loadJobFail(state, action);

      case JobActionTypes.CREATE_JOB:
        return this.createJob(state, action);

      case JobActionTypes.CREATE_JOB_DONE:
        return this.createJobDone(state, action);

      case JobActionTypes.CREATE_JOB_FAIL:
        return this.createJobFail(state, action);

      case JobActionTypes.UPDATE_JOB:
        return this.updateJob(state, action);

      case JobActionTypes.UPDATE_JOB_DONE:
        return this.updateJobDone(state, action);

      case JobActionTypes.UPDATE_JOB_FAIL:
        return this.updateJobFail(state, action);

      case JobActionTypes.DELETE_JOB:
        return this.deleteJob(state, action);

      case JobActionTypes.DELETE_JOB_DONE:
        return this.deleteJobDone(state, action);

      case JobActionTypes.DELETE_JOB_FAIL:
        return this.deleteJobFail(state, action);

      case JobActionTypes.CLONE_JOB:
        return this.cloneJob(state, action);

      case JobActionTypes.CLONE_JOB_DONE:
        return this.cloneJobDone(state, action);

      case JobActionTypes.CLONE_JOB_FAIL:
        return this.cloneJobFail(state, action);

      case JobActionTypes.LOAD_JOB_FORM_DROPDOWNS:
        return this.loadJobFormDropdowns(state, action);

      case JobActionTypes.LOAD_JOB_FORM_DROPDOWNS_DONE:
        return this.loadJobFormDropdownsDone(state, action);

      case JobActionTypes.LOAD_JOB_FORM_DROPDOWNS_FAIL:
        return this.loadJobFormDropdownsFail(state, action);

      case JobActionTypes.SWITCH_JOB_SCOPE_SIZE:
        return this.switchJobScopeSize(state, action);

      case JobActionTypes.SWITCH_CANDIDATE_SCOPE_SIZE:
        return this.switchCandidateScopeSize(state, action);

      case JobActionTypes.LOAD_CANDIDATES:
        return this.loadCandidates(state, action);

      case JobActionTypes.LOAD_CANDIDATES_DONE:
        return this.loadCandidatesDone(state, action);

      case JobActionTypes.LOAD_LEAD_JOB_APPLICATIONS:
        return this.loadLeadJobApplications(state, action);

      case JobActionTypes.LOAD_LEAD_JOB_APPLICATIONS_DONE:
        return this.loadLeadJobApplicationsDone(state, action);

      case JobActionTypes.LOAD_LEAD_JOB_APPLICATIONS_FAIL:
        return this.loadLeadJobApplicationsFail(state, action);

      case JobActionTypes.LOAD_CANDIDATES_FAIL:
        return this.loadCandidatesFail(state, action);

      case JobActionTypes.BULK_REMOVE_CANDIDATES_FROM_JOB:
        return this.bulkRemoveCandidatesFromJob(state, action);

      case JobActionTypes.BULK_REMOVE_CANDIDATES_FROM_JOB_DONE:
        return this.bulkRemoveCandidatesFromJobDone(state, action);

      case JobActionTypes.BULK_REMOVE_CANDIDATES_FROM_JOB_FAIL:
        return this.bulkRemoveCandidatesFromJobFail(state, action);

      case JobActionTypes.BULK_UPDATE_CANDIDATES_FROM_JOB:
        return this.bulkUpdateCandidatesFromJob(state, action);

      case JobActionTypes.BULK_UPDATE_CANDIDATES_FROM_JOB_DONE:
        return this.bulkUpdateCandidatesFromJobDone(state, action);

      case JobActionTypes.BULK_UPDATE_CANDIDATES_FROM_JOB_FAIL:
        return this.bulkUpdateCandidatesFromJobFail(state, action);

      default:
        return state;
    }
  }

  loadJobs(state, action) {
    const newState = { ...state };
    const { table } = action;
    newState.scopedLoadings[table.scope] = true;
    newState.lastJobStoreAction = 'loadJobs';
    return newState;
  }

  loadJobsDone(state, action) {
    const newState = { ...state };
    const { table, response, query } = action;
    newState.scopedLoadings[table.scope] = false;
    newState.scopedRecords[table.scope] = response;
    newState.scopedQueries[table.scope] = query;
    newState.pagination = response.pagination;
    newState.lastJobStoreAction = 'loadJobsDone';
    return newState;
  }

  loadJobsFail(state, action) {
    const newState = { ...state };
    const { table, error } = action;
    newState.scopedLoadings[table.scope] = false;
    newState.errors = error;
    newState.lastJobStoreAction = 'loadJobsFail';
    return newState;
  }

  loadJob(state, action) {
    const newState = { ...state };
    newState.loadingJob = true;
    newState.lastJobStoreAction = 'loadJob';
    return newState;
  }

  loadJobDone(state, action) {
    const newState = { ...state };
    const { job } = action;
    newState.loadingJob = false;
    newState.job = job;
    newState.errors = {};
    newState.lastJobStoreAction = 'loadJobDone';
    return newState;
  }

  loadJobFail(state, action) {
    const newState = { ...state };
    const { error } = action;
    newState.loadingJob = false;
    newState.errors = error;
    newState.lastJobStoreAction = 'loadJobFail';
    return newState;
  }

  createJob(state, action) {
    const newState = { ...state };
    newState.submitting = true;
    newState.lastJobStoreAction = 'createJob';
    return newState;
  }

  createJobDone(state, action) {
    const newState = { ...state };
    newState.submitting = false;
    newState.errors = {};
    newState.lastJobStoreAction = 'createJobDone';
    newState.job = action.job;
    return newState;
  }

  createJobFail(state, action) {
    const newState = { ...state };
    const { error } = action;
    newState.submitting = false;
    newState.errors = error;
    newState.lastJobStoreAction = 'createJobFail';
    return newState;
  }

  updateJob(state, action) {
    const newState = { ...state };
    newState.lastJobStoreAction = 'updateJob';
    return newState;
  }

  updateJobDone(state, action) {
    const newState = { ...state };
    newState.errors = {};
    newState.lastJobStoreAction = 'updateJobDone';
    newState.job = action.job;
    return newState;
  }

  updateJobFail(state, action) {
    const newState = { ...state };
    const { error } = action;
    newState.errors = error;
    newState.lastJobStoreAction = 'updateJobFail';
    return newState;
  }

  deleteJob(state, action) {
    const newState = { ...state };
    newState.lastJobStoreAction = 'deleteJob';
    return newState;
  }

  deleteJobDone(state, action) {
    const newState = { ...state };
    newState.errors = {};
    newState.lastJobStoreAction = 'deleteJobDone';
    return newState;
  }

  deleteJobFail(state, action) {
    const newState = { ...state };
    const { error } = action;
    newState.errors = error;
    newState.lastJobStoreAction = 'deleteJobFail';
    return newState;
  }

  cloneJob(state) {
    const newState = { ...state };
    newState.submitting = true;
    newState.errors = {};
    newState.lastJobStoreAction = 'cloneJob';
    return newState;
  }

  cloneJobDone(state, action) {
    const newState = { ...state };
    const { job } = action;
    newState.submitting = false;
    newState.job = job;
    newState.errors = {};
    newState.lastJobStoreAction = 'cloneJobDone';
    return newState;
  }

  cloneJobFail(state, action) {
    const newState = { ...state };
    const { error } = action;
    newState.submitting = false;
    newState.errors = error;
    newState.lastJobStoreAction = 'cloneJobFail';
    return newState;
  }

  loadJobFormDropdowns(state, action) {
    const newState = { ...state };
    newState.lastJobStoreAction = 'loadJobFormDropdowns';
    return newState;
  }

  loadJobFormDropdownsDone(state, action) {
    const newState = { ...state };
    const { dropdowns } = action;
    newState.jobFormDropdownsData = dropdowns;
    newState.lastJobStoreAction = 'loadJobFormDropdownsDone';
    return newState;
  }

  loadJobFormDropdownsFail(state, action) {
    const newState = { ...state };
    const { error } = action;
    newState.errors = error;
    newState.lastJobStoreAction = 'loadJobFormDropdownsFail';
    return newState;
  }

  switchJobScopeSize(state, action) {
    const newState = { ...state };
    const { jobScopeSize } = action;
    newState.jobScopeSize = jobScopeSize;
    newState.lastJobStoreAction = 'switchJobScopeSize';
    return newState;
  }

  switchCandidateScopeSize(state, action) {
    const newState = { ...state };
    const { candidateScopeSize } = action;
    newState.candidateScopeSize = candidateScopeSize;
    newState.lastJobStoreAction = 'switchCandidateScopeSize';
    return newState;
  }

  loadCandidates(state, action) {
    const newState = { ...state };
    const { table } = action;
    newState.scopedLoadings[table.scope] = true;
    newState.lastJobStoreAction = 'loadCandidates';
    return newState;
  }

  loadCandidatesDone(state, action) {
    const newState = { ...state };
    const { table, response, query } = action;
    newState.scopedLoadings[table.scope] = false;
    newState.scopedRecords[table.scope] = response;
    newState.scopedQueries[table.scope] = query;
    newState.pagination = response.pagination;
    newState.lastJobStoreAction = 'loadCandidatesDone';
    return newState;
  }

  loadCandidatesFail(state, action) {
    const newState = { ...state };
    const { table, error } = action;
    newState.scopedLoadings[table.scope] = false;
    newState.errors = error;
    newState.lastJobStoreAction = 'loadCandidatesFail';
    return newState;
  }

  loadLeadJobApplications(state, action) {
    const newState = { ...state };
    newState.lastJobStoreAction = 'loadLeadJobApplications';
    return newState;
  }

  loadLeadJobApplicationsDone(state, action) {
    const newState = { ...state };
    const { response, query } = action;
    newState.leadJobApplications = response.candidates;
    newState.leadJobApplicationsQuery = query;
    newState.pagination = response.pagination;
    newState.lastJobStoreAction = 'loadLeadJobApplicationsDone';
    return newState;
  }

  loadLeadJobApplicationsFail(state, action) {
    const newState = { ...state };
    const { error } = action;
    newState.errors = error;
    newState.lastJobStoreAction = 'loadLeadJobApplicationsFail';
    return newState;
  }

  bulkRemoveCandidatesFromJob(state, action) {
    const newState = { ...state };
    const { tableScope } = action;
    newState.scopedLoadings[tableScope] = true;
    newState.lastJobStoreAction = 'bulkRemoveCandidatesFromJob';
    return newState;
  }

  bulkRemoveCandidatesFromJobDone(state, action) {
    const newState = { ...state };
    const { tableScope } = action;
    newState.scopedLoadings[tableScope] = false;
    newState.errors = {};
    newState.lastJobStoreAction = 'bulkRemoveCandidatesFromJobDone';
    return newState;
  }

  bulkRemoveCandidatesFromJobFail(state, action) {
    const newState = { ...state };
    const { tableScope, error } = action;
    newState.scopedLoadings[tableScope] = false;
    newState.errors = error;
    newState.lastJobStoreAction = 'bulkRemoveCandidatesFromJobFail';
    return newState;
  }

  bulkUpdateCandidatesFromJob(state, action) {
    const newState = { ...state };
    const { tableScope } = action;
    newState.scopedLoadings[tableScope] = true;
    newState.lastJobStoreAction = 'bulkUpdateCandidatesFromJob';
    return newState;
  }

  bulkUpdateCandidatesFromJobDone(state, action) {
    const newState = { ...state };
    const { tableScope } = action;
    newState.scopedLoadings[tableScope] = false;
    newState.errors = {};
    newState.lastJobStoreAction = 'bulkUpdateCandidatesFromJobDone';
    return newState;
  }

  bulkUpdateCandidatesFromJobFail(state, action) {
    const newState = { ...state };
    const { tableScope, error } = action;
    newState.scopedLoadings[tableScope] = false;
    newState.errors = error;
    newState.lastJobStoreAction = 'bulkUpdateCandidatesFromJobFail';
    return newState;
  }
}

export default new JobStore();
